import React, {
  useCallback,
} from 'react';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import Alert from '@mui/material/Alert';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import printJS from 'print-js';
import upperFirst from 'lodash/upperFirst';
import noop from 'lodash/noop';
import compact from 'lodash/compact';
import {
  useForm,
} from 'react-hook-form';
import Controller from 'core/components/FormController';
import Tooltip from 'core/components/Tooltip';
import {
  useNotify,
} from 'core/hooks';
import {
  formatDateTime,
} from 'utils/date';
import {
  formatMoney,
} from '@populateui/utils';
import Button from 'pages/Dashboard/components/Button';
import Dialog from 'pages/Dashboard/components/Dialog';
import Input from 'pages/Dashboard/components/Input';
import TextAreaWithActions from 'pages/Dashboard/components/TextAreaWithActions';
import {
  Patient,
} from 'pages/Dashboard/types/patient';
import {
  validateEmail,
} from 'pages/Dashboard/utils/helper';
import {
  useGetInvoiceCommuniqueInfo,
  usePrintPatientStatement,
  useSendPatientStatement,
} from 'pages/Dashboard/pages/Billing/hooks';

const actions = ['print', 'send'] as const;
export type Action = typeof actions[number];

type StatementForm = {
  comment?: string;
  email?: string;
};

export type StatementDialogParams = {
  type: Action;
  patient: Patient;
  visits?: number[];
  excludeEncountersWithNoDues?: boolean;
};

type Props = StatementDialogParams & {
  onClose: () => void;
};

export default function StatementFormDialog({
  type,
  patient,
  onClose,
  visits,
  excludeEncountersWithNoDues,
}: Props) {
  const { isBillingContact, email: patientContactEmail } = patient?.otherContact ?? {};
  const {
    control,
    formState: { isValid },
    handleSubmit,
  } = useForm<StatementForm>({
    mode: 'all',
    defaultValues: { email: (isBillingContact ? patientContactEmail : patient?.email) ?? '' },
  });

  const notify = useNotify();
  const {
    data: communiqueInfo,
  } = useGetInvoiceCommuniqueInfo(patient?.patientId ?? 0);

  const {
    isPending: isPrinting,
    mutateAsync: printStatement,
  } = usePrintPatientStatement();

  const {
    isPending: isSending,
    mutateAsync: sendStatement,
  } = useSendPatientStatement();

  const onSubmit = useCallback(async ({ comment, email }: StatementForm) => {
    const payload = {
      patientId: patient.patientId,
      encounterIds: compact(visits),
      comment,
      email,
      excludeEncountersWithNoDues,
    };
    if (type === 'send') {
      await sendStatement(payload);
      notify({
        message: 'Statement sent successfully!',
        variant: 'success',
      });
    } else {
      const response = await printStatement(payload);
      printJS({
        printable: response?.list?.[0]?.url?.value,
        type: 'pdf',
        showModal: true,
      });
    }
    onClose?.();
  }, [type, visits, patient]);

  const { totalBalance = 0, lastSentOn } = communiqueInfo ?? {};
  const actionText = upperFirst(type);
  const isLoading = isSending || isPrinting;

  return (
    <Dialog
      open
      title={`${actionText} Statement`}
      onClose={onClose}
      onConfirm={noop}
      disableEscapeKeyDown
      disableBackdropClick
      maxWidth="md"
      className="populate-form"
    >
      <div className="flex flex-col gap-form-row">
        {!isNil(lastSentOn) && (
          <Alert
            severity="info"
            icon={<InfoIcon className="!w-4 !h-4 mt-1" />}
          >
            {'Patient Statement last sent at '}
            <span className="font-semibold">{formatDateTime(lastSentOn)}</span>
            {' for '}
            <span className="font-semibold">{formatMoney(totalBalance)}</span>
          </Alert>
        )}
        {type === 'send' && (
          <Controller
            control={control}
            name="email"
            rules={{ required: true, validate: validateEmail }}
            render={({ field, fieldState: { invalid } }) => (
              <Input
                {...field}
                required
                id="email"
                error={invalid}
                showErrorText={false}
                className="w-full"
                label="Email"
                placeholder="Enter Email"
              />
            )}
          />
        )}
        <Controller
          name="comment"
          control={control}
          render={({ field }) => (
            <TextAreaWithActions
              {...field}
              className="mt-3"
              placeholder="Add Comment"
              textAreaClassName="pb-8"
              rows={3}
              id="statement-comment"
              trackingLabel="Statement Comment"
              label="Comment"
            />
          )}
        />
      </div>
      <div className="flex gap-4 justify-end">
        <Button
          variant="outlined"
          type="button"
          onClick={onClose}
        >
          Close
        </Button>
        <Tooltip
          title="Email is missing"
          hide={isValid}
        >
          <div className={clsx('h-full', { 'cursor-not-allowed': !isValid })}>
            <Button
              variant="contained"
              type="button"
              color="primary"
              className="!h-full"
              disabled={!isValid || isLoading}
              onClick={handleSubmit(onSubmit)}
            >
              {isLoading ? `${actionText}...` : actionText}
            </Button>
          </div>
        </Tooltip>
      </div>
    </Dialog>
  );
}
